import ajax from './ajax';

export interface Question {
    _id: string;
    questionId: string;
    title: string;
    text: string;
    type: string;
    category?: string[];
    tags?: string[];
    difficulty?: string;
    options?: any[];
    correctAnswer?: any;
    explanation?: string;
    defaultMarks: number;
    defaultTimeSeconds?: number;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateQuestionData {
    title: string;
    text: string;
    type: string;
    category?: string[];
    tags?: string[];
    difficulty?: string;
    options?: any[];
    correctAnswer?: any;
    explanation?: string;
    defaultMarks: number;
    defaultTimeSeconds?: number;
    status?: string;
    publishedAt?: string;
    visibility?: string;
}

export interface SearchQuestionsParams {
    page?: number;
    limit?: number;
    /** Full-text search — the backend reads the `query` param. */
    query?: string;
    type?: string;
    category?: string;
    difficulty?: string;
    tags?: string[];
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface QuestionListResponse {
    questions: Question[];
    total: number;
    page: number;
    limit: number;
}

class QuestionService {
    async searchQuestions(params: SearchQuestionsParams = {}): Promise<QuestionListResponse> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((v) => queryParams.append(key, String(v)));
                } else {
                    queryParams.append(key, String(value));
                }
            }
        });
        const query = queryParams.toString();
        return ajax.get(`/api/questions${query ? `?${query}` : ''}`);
    }

    async getQuestion(id: string): Promise<Question> {
        return ajax.get(`/api/questions/${id}`);
    }

    async createQuestion(data: CreateQuestionData): Promise<Question> {
        return ajax.post('/api/questions', data);
    }

    async updateQuestion(id: string, data: Partial<CreateQuestionData>): Promise<Question> {
        return ajax.put(`/api/questions/${id}`, data);
    }

    async deleteQuestion(id: string): Promise<{ message: string }> {
        return ajax.delete(`/api/questions/${id}`);
    }

    async cloneQuestion(id: string): Promise<Question> {
        return ajax.post(`/api/questions/${id}/clone`);
    }

    async uploadMedia(questionId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        return ajax.post(`/api/questions/${questionId}/media`, formData, {
            headers: {}, // Let browser set Content-Type for multipart
        });
    }

    async submitForApproval(id: string): Promise<{ message: string }> {
        return ajax.post(`/api/questions/${id}/submit-for-approval`);
    }

    async approveQuestion(id: string): Promise<{ message: string }> {
        return ajax.post(`/api/questions/${id}/approve`);
    }

    async rejectQuestion(id: string, reason?: string): Promise<{ message: string }> {
        return ajax.post(`/api/questions/${id}/reject`, { reason });
    }

    async getPendingQuestions(params?: SearchQuestionsParams): Promise<QuestionListResponse> {
        return this.searchQuestions({ ...params, status: 'pending_review' } as any);
    }

    async archiveQuestion(id: string): Promise<Question> {
        return ajax.post(`/api/questions/${id}/archive`);
    }

    async unarchiveQuestion(id: string): Promise<Question> {
        return ajax.post(`/api/questions/${id}/unarchive`);
    }

    async validateWithAI(id: string): Promise<any> {
        return ajax.post(`/api/questions/${id}/validate-ai`);
    }

    async getQuestionVersions(id: string): Promise<{ versions: Question[] }> {
        return ajax.get(`/api/questions/${id}/versions`);
    }

    /** Downloads the caller's question bank as a CSV file. */
    async exportQuestionsCSV(): Promise<void> {
        const csv: string = await ajax.get('/api/questions/export-csv');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'question-bank.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    async importQuestionsFromCSV(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        return ajax.post('/api/questions/import-csv', formData, {
            headers: {},
        });
    }

    generateSampleCSV(): string {
        const headers = [
            'title',
            'text',
            'type',
            'defaultMarks',
            'defaultTimeSeconds',
            'difficulty',
            'visibility',
            'category',
            'tags',
            'options',
            'correctAnswer'
        ];

        const sampleRows = [
            [
                'What is React?',
                'React is a JavaScript library for building user interfaces.',
                'mcq_single',
                '1',
                '60',
                'beginner',
                'private',
                'react-category-id',
                'react;javascript;frontend',
                'A JavaScript library|true;A database|false;A framework|false;An API|false',
                ''
            ],
            [
                'Capital of France',
                'What is the capital city of France?',
                'fill_blank',
                '1',
                '30',
                'easy',
                'public',
                'geography-category-id',
                'geography;capitals',
                '',
                'Paris'
            ]
        ];

        const csvContent = [
            headers.join(','),
            ...sampleRows.map(row => row.join(','))
        ].join('\n');

        return csvContent;
    }

    downloadSampleCSV(): void {
        const csvContent = this.generateSampleCSV();
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'questions_sample.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export default new QuestionService();
